import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJsApiLoader } from '@react-google-maps/api';
import { WizardStepper } from '../../components/layout/WizardStepper';
import { AppNav } from '../../components/layout/AppNav';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { LocationMap } from '../../components/maps/LocationMap';
import { useWizardStore } from '../../store/wizard.store';
import { locationsService } from '../../services/locations.service';
import api from '../../services/api';
import { KSA_REGIONS, KSA_CITIES } from '../../data/ksa-regions-cities';

const WIZARD_STEPS = [
  { label: 'Product', path: '/catalog' },
  { label: 'Location & Coverage', path: '/offers/wizard/location' },
  { label: 'Cart', path: '/offers/wizard/cart' },
  { label: 'Customer', path: '/offers/wizard/customer' },
  { label: 'Auth. Person', path: '/offers/wizard/authorized-person' },
  { label: 'Summary', path: '/offers/wizard/summary' },
];

const LIBRARIES: ('places')[] = ['places'];

interface FieldErrors {
  location?: string;
  city?: string;
  region?: string;
}

export const LocationStep: React.FC = () => {
  const navigate = useNavigate();
  const { selectedProduct, offerId, addItem, setStep } = useWizardStore();
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [coverageStatus, setCoverageStatus] = useState<'Available' | 'Unavailable' | 'Checking' | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const coverageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autocompleteInputRef = useRef<HTMLInputElement>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAPS_KEY ?? '',
    libraries: LIBRARIES,
  });

  useEffect(
    () => () => {
      if (coverageTimeoutRef.current) clearTimeout(coverageTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!isLoaded || !autocompleteInputRef.current) return;
    const ac = new google.maps.places.Autocomplete(autocompleteInputRef.current, {
      componentRestrictions: { country: 'sa' },
      fields: ['geometry', 'formatted_address'],
    });
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place.geometry?.location) return;
      const newLat = place.geometry.location.lat();
      const newLng = place.geometry.location.lng();
      setLat(newLat.toFixed(6));
      setLng(newLng.toFixed(6));
      setAddressLine1(place.formatted_address ?? '');
      setMapCenter({ lat: newLat, lng: newLng });
      setFieldErrors((e) => ({ ...e, location: undefined }));
      triggerCoverageCheck(newLat, newLng);
    });
    return () => {
      google.maps.event.clearInstanceListeners(ac);
    };
  }, [isLoaded]);

  useEffect(() => {
    if (!selectedProduct || !offerId) navigate('/catalog');
  }, [selectedProduct, offerId, navigate]);

  if (!selectedProduct || !offerId) return null;

  const needsCoverage = Boolean(selectedProduct.requiresCoverage) && selectedProduct.category !== 'Mobility';
  const cityOptions = region ? (KSA_CITIES[region] ?? []) : [];

  const triggerCoverageCheck = (latNum: number, lngNum: number) => {
    if (!needsCoverage) return;
    if (coverageTimeoutRef.current) clearTimeout(coverageTimeoutRef.current);
    setCoverageStatus('Checking');
    coverageTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await locationsService.checkCoverage(latNum, lngNum, selectedProduct.category);
        setCoverageStatus(result.status);
      } catch {
        setCoverageStatus(null);
      }
    }, 600);
  };

  const handleMapSelect = (selLat: number, selLng: number) => {
    setLat(selLat.toFixed(6));
    setLng(selLng.toFixed(6));
    setFieldErrors((e) => ({ ...e, location: undefined }));
    triggerCoverageCheck(selLat, selLng);
  };

  const validate = (): boolean => {
    const errors: FieldErrors = {};
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (isNaN(latNum) || isNaN(lngNum)) {
      errors.location = 'Please search for an address or click on the map to set a location';
    }
    if (!city.trim()) errors.city = 'City is required';
    if (!region.trim()) errors.region = 'Region is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddAndContinue = async () => {
    if (!validate()) return;

    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    setSaving(true);
    try {
      const location = (await locationsService.create({
        offerId,
        lat: latNum,
        lng: lngNum,
        addressLine1: addressLine1.trim() || undefined,
        city,
        region,
      })) as { id: string };

      await api.post(`/offers/${offerId}/cart`, {
        productId: selectedProduct.id,
        locationId: location.id,
        configJson: {},
      });

      const finalCoverageStatus = !needsCoverage
        ? 'Available'
        : coverageStatus === 'Available'
        ? 'Available'
        : coverageStatus === 'Unavailable'
        ? 'Unavailable'
        : 'Pending';

      addItem({
        productId: selectedProduct.id,
        product: selectedProduct,
        locationId: location.id,
        location: {
          lat: latNum,
          lng: lngNum,
          addressLine1: addressLine1.trim() || '',
          city,
          region,
          coverageStatus: finalCoverageStatus,
        },
      });
      setStep(2);
      navigate('/offers/wizard/cart');
    } catch {
      setFieldErrors((e) => ({ ...e, location: 'Failed to save location — please try again' }));
    } finally {
      setSaving(false);
    }
  };

  let coverageBanner: React.ReactNode = null;
  if (!needsCoverage) {
    coverageBanner = (
      <div style={{ background: 'var(--succ-bg)', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '12px 16px', color: '#166534', fontSize: '13.5px' }}>
        ℹ Coverage check not required for this product type
      </div>
    );
  } else if (coverageStatus === 'Checking') {
    coverageBanner = (
      <div style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 16px', color: 'var(--text-2)', fontSize: '13.5px' }}>
        Checking coverage…
      </div>
    );
  } else if (coverageStatus === 'Available') {
    coverageBanner = (
      <div style={{ background: 'var(--succ-bg)', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '12px 16px', color: '#166534', fontSize: '13.5px', fontWeight: 600 }}>
        ✓ Coverage available at this location
      </div>
    );
  } else if (coverageStatus === 'Unavailable') {
    coverageBanner = (
      <div style={{ background: 'var(--err-bg)', border: '1px solid var(--err-border)', borderRadius: '8px', padding: '12px 16px', color: 'var(--err-hover)', fontSize: '13.5px' }}>
        ✗ No coverage at this location — you can still add but it will be marked as failed
      </div>
    );
  }

  const mapCoverageStatus = !needsCoverage
    ? 'Available'
    : coverageStatus === 'Checking'
    ? 'Pending'
    : coverageStatus ?? undefined;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Location & Coverage" subtitle={`Product: ${selectedProduct.name}`} />
      <WizardStepper steps={WIZARD_STEPS} currentStep={1} />
      <main style={{ flex: 1, padding: '28px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '800px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Address search box */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
              Search Service Address
            </label>
            <input
              ref={autocompleteInputRef}
              type="text"
              placeholder={isLoaded ? 'Search for an address in Saudi Arabia…' : 'Loading map…'}
              disabled={!isLoaded}
              className="inp"
              style={{ background: isLoaded ? 'var(--surface)' : 'var(--surface-alt)' }}
            />
          </div>

          {/* Map */}
          <div>
            <LocationMap
              onLocationSelect={handleMapSelect}
              coverageStatus={mapCoverageStatus}
              center={mapCenter}
            />
            {fieldErrors.location && (
              <span style={{ display: 'block', fontSize: '12px', color: '#dc3545', marginTop: '6px' }}>
                {fieldErrors.location}
              </span>
            )}
          </div>

          {coverageBanner}

          {/* Address fields */}
          <div className="card" style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Read-only coordinates filled from map click or address search */}
            <FormField
              label="Latitude"
              value={lat}
              readOnly
              placeholder="Set via search or map click"
              style={{ background: '#f8f9fa' }}
            />
            <FormField
              label="Longitude"
              value={lng}
              readOnly
              placeholder="Set via search or map click"
              style={{ background: '#f8f9fa' }}
            />

            <div style={{ gridColumn: '1 / -1' }}>
              <FormField
                label="Service Address"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                placeholder="Street, building, floor (optional)"
              />
            </div>

            <SearchableSelect
              label="Region"
              options={KSA_REGIONS}
              value={region}
              onChange={(v) => {
                setRegion(v);
                setCity('');
                setFieldErrors((e) => ({ ...e, region: undefined }));
              }}
              placeholder="Select region…"
              required
              error={fieldErrors.region}
            />

            <SearchableSelect
              label="City"
              options={cityOptions}
              value={city}
              onChange={(v) => {
                setCity(v);
                setFieldErrors((e) => ({ ...e, city: undefined }));
              }}
              placeholder={region ? 'Select city…' : 'Select region first'}
              disabled={!region}
              required
              error={fieldErrors.city}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button variant="ghost" onClick={() => navigate('/my-offers')}>
                Save & Exit
              </Button>
              <Button variant="ghost" onClick={() => navigate('/catalog')}>
                ← Back to Catalog
              </Button>
            </div>
            <Button onClick={handleAddAndContinue} loading={saving}>
              Add to Cart & Continue
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};
